import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { useState } from "react";
import type {
  CatalogRepository,
  Product,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RejectionNotice } from "@/modules/catalog/screens/RejectionNotice";

type ProductPatch = Parameters<CatalogRepository["updateProduct"]>[1];

/**
 * What is being typed, and the `channelUpdatedAt` this screen observed when
 * editing started — captured once, at the moment "Edit details" is clicked,
 * never re-read from `product` afterwards. The same reasoning `HsnEditor`'s
 * `EditState` gives: `product` is a query result a background refetch can
 * silently replace while the form is open, and reading the guard fresh at
 * submit time would defeat the conflict check (#166).
 */
interface Draft {
  title: string;
  descriptionHtml: string;
  productType: string;
  vendor: string;
  /** Comma-separated for editing; split and trimmed on submit. */
  tags: string;
  guard: string;
}

function toDraft(product: Product): Draft {
  return {
    title: product.title,
    descriptionHtml: product.descriptionHtml ?? "",
    productType: product.productType ?? "",
    vendor: product.vendor ?? "",
    tags: product.tags.join(", "),
    guard: product.channelUpdatedAt,
  };
}

/**
 * Only the fields that actually changed — #170's ticket text: "sending only
 * the changed fields". An unedited field is left out of the patch entirely
 * rather than sent back unchanged, the same rule `setOrderNote` follows for
 * a note.
 */
function patchOf(product: Product, draft: Draft): ProductPatch {
  const patch: ProductPatch = {};

  const title = draft.title.trim();
  if (title !== product.title) patch.title = title;

  const descriptionHtml =
    draft.descriptionHtml.trim() === "" ? null : draft.descriptionHtml;
  if (descriptionHtml !== (product.descriptionHtml ?? null)) {
    patch.descriptionHtml = descriptionHtml;
  }

  const productType =
    draft.productType.trim() === "" ? null : draft.productType.trim();
  if (productType !== (product.productType ?? null))
    patch.productType = productType;

  const vendor = draft.vendor.trim() === "" ? null : draft.vendor.trim();
  if (vendor !== (product.vendor ?? null)) patch.vendor = vendor;

  const tags = draft.tags
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag !== "");
  if (tags.join("\n") !== product.tags.join("\n")) patch.tags = tags;

  return patch;
}

/**
 * Title, description, product type, vendor and tags, written through to
 * Shopify's `productUpdate` (FR-CAT-7, #170).
 *
 * The write path, not the inputs, is the point: `updateProduct` takes a
 * `Guarded` and returns a `WriteResult` rather than throwing, and a rejected
 * write never touches `editing` or `product` — the form stays open with
 * every field still holding what was typed, and the rejection shown, because
 * nothing here ever writes the draft into local state (write-through, never
 * write-local).
 */
export function ProductEditForm({ product }: { product: Product }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canEdit = can(permissions, "catalog.product.edit");

  const [editing, setEditing] = useState<Draft | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (draft: Draft) =>
      data.catalog.updateProduct(product.id, patchOf(product, draft), {
        expectedUpdatedAt: draft.guard,
      }),
    onSuccess: (result) => {
      if (result.ok) {
        setEditing(null);
        setRejection(null);
        // Write-through, never write-local: the screen redraws from what this
        // invalidation fetches back, never from the draft — the same reason
        // `HsnEditor` invalidates the whole "catalog" key rather than one
        // query, so the product list picks up a renamed title immediately.
        void queryClient.invalidateQueries({ queryKey: ["catalog"] });
      } else {
        setRejection(result.rejection);
      }
    },
  });

  if (!canEdit) return null;

  if (editing === null) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          setRejection(null);
          setEditing(toDraft(product));
        }}
      >
        <Pencil />
        Edit details
      </Button>
    );
  }

  function submit() {
    if (!editing) return;
    const patch = patchOf(product, editing);
    if (Object.keys(patch).length === 0) {
      // Nothing changed — closed without a round trip rather than sent as a
      // write only to be told "Nothing to change" by the server.
      setEditing(null);
      setRejection(null);
      return;
    }
    setRejection(null);
    mutation.mutate(editing);
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="product-edit-title">Title</Label>
          <Input
            id="product-edit-title"
            value={editing.title}
            disabled={mutation.isPending}
            onChange={(event) => {
              setEditing({ ...editing, title: event.target.value });
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="product-edit-vendor">Vendor</Label>
          <Input
            id="product-edit-vendor"
            value={editing.vendor}
            disabled={mutation.isPending}
            onChange={(event) => {
              setEditing({ ...editing, vendor: event.target.value });
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="product-edit-type">Product type</Label>
          <Input
            id="product-edit-type"
            value={editing.productType}
            disabled={mutation.isPending}
            onChange={(event) => {
              setEditing({ ...editing, productType: event.target.value });
            }}
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="product-edit-tags">Tags</Label>
          <Input
            id="product-edit-tags"
            value={editing.tags}
            placeholder="Comma-separated — B12, imitation-jewellery"
            disabled={mutation.isPending}
            onChange={(event) => {
              setEditing({ ...editing, tags: event.target.value });
            }}
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="product-edit-description">Description (HTML)</Label>
          <Textarea
            id="product-edit-description"
            rows={5}
            value={editing.descriptionHtml}
            disabled={mutation.isPending}
            onChange={(event) => {
              setEditing({ ...editing, descriptionHtml: event.target.value });
            }}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={editing.title.trim() === "" || mutation.isPending}
          onClick={submit}
        >
          {mutation.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Check />
          )}
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={mutation.isPending}
          onClick={() => {
            setEditing(null);
            setRejection(null);
          }}
        >
          <X />
          Cancel
        </Button>
      </div>

      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            void queryClient.invalidateQueries({ queryKey: ["catalog"] });
            setEditing(null);
            setRejection(null);
          }}
        />
      ) : null}
    </div>
  );
}
